import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import API_CONFIG from './config';
import MermaidLive from './MermaidChart/MermaidLive';
import { extractMermaidDiagrams, removeMermaidDiagrams } from './utils/mermaid-helpers';
import MicrophoneModal from './MicrophoneModal';
import Geohublogo from './Images/Geohublogoo.png';
import Icons from './Icons';
import MarkdownWithMath from './MermaidChart/markdownwithmath';
import { MathJaxContext } from 'better-react-mathjax';
import { useNavigate } from 'react-router-dom';
import { updateConversationTitle } from './utils/apiServices';

const config = {
  tex: {
    packages: ['base', 'ams'],
  },
};

const ConversationView = ({ conversation, isShared = false }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [previewPos, setPreviewPos] = useState({ x: 0, y: 0 });
  const messagesContainerRef = useRef(null);
  const shouldScrollRef = useRef(true);
  const scrollTimeout = useRef(null);
  const abortControllerRef = useRef(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const lastScrollTop = useRef(0);
  const shouldAutoScroll = useRef(true);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isMicModalOpen, setIsMicModalOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [transcriptionText, setTranscriptionText] = useState('');
  const audioStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const webSocketRef = useRef(null);
  const audioContextRef = useRef(null);
  const pingIntervalRef = useRef(null);

  // Keep track of the current conversation ID to detect actual conversation changes
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [isForked, setIsForked] = useState(false);
  const [forkedConversationId, setForkedConversationId] = useState(null);
  const [currentConversation, setCurrentConversation] = useState(conversation);
  const [isForking, setIsForking] = useState(false);
  const [isReadyForChat, setIsReadyForChat] = useState(!isShared); // Shared conversations start as not ready
  const inputRef = useRef(null);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const navigate = useNavigate();

  // Check authentication status for shared conversations
  useEffect(() => {
    if (isShared) {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        setShowLoginModal(true);
      } else {
        setShowLoginModal(false);
      }
    }
  }, [isShared]);

  // Check authentication status when component mounts or user returns from login
  useEffect(() => {
    const checkAuth = () => {
      if (isShared) {
        const authToken = localStorage.getItem('authToken');
        if (authToken) {
          setShowLoginModal(false);
        }
      }
    };

    // Check immediately
    checkAuth();

    // Listen for storage changes (when user logs in/out)
    const handleStorageChange = (e) => {
      if (e.key === 'authToken') {
        checkAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isShared]);

  // Process content to remove colons from labels and handle Mermaid diagrams
  const processContent = (content) => {
    if (!content) return content;

    // Replace patterns like "Type: " with "Type " (removing the colon)
    // This will match any word followed by a colon and space, including those at the start of lines and in the middle of text
    let processedContent = content.replace(/([A-Za-z]+):\s/g, '$1 ');

    // Preprocess math expressions to ensure proper delimiter handling
    // Replace single $ with $$ if they appear to be unpaired (basic heuristic)
    const parts = processedContent.split('$$');
    if (parts.length === 2) {
      // If there's only one pair of $$, it might be a single line math expression
      processedContent = parts.join('$$');
    } else {
      // Check for single $ and attempt to pair them if possible
      let inMath = false;
      let result = '';
      for (let i = 0; i < processedContent.length; i++) {
        if (processedContent[i] === '$') {
          if (inMath) {
            result += '$';
            inMath = false;
          } else if (i + 1 < processedContent.length && processedContent[i + 1] === '$') {
            result += '$$';
            i++; // Skip the next $
            inMath = false;
          } else {
            result += '$';
            inMath = true;
          }
        } else {
          result += processedContent[i];
        }
      }
      processedContent = result;
    }

    // Don't remove Mermaid diagrams anymore, let the ReactMarkdown code component handle it
    return processedContent;
  };

  // Function to scroll to bottom of messages
  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current && shouldAutoScroll.current) {
      const { scrollHeight, clientHeight } = messagesContainerRef.current;
      messagesContainerRef.current.scrollTop = scrollHeight - clientHeight;
    }
  }, []);

  // Check if user has manually scrolled up
  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const currentScrollTop = scrollTop;
    const isAtBottom = scrollHeight - scrollTop - clientHeight <= 50;

    // Clear any existing timeout
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }

    // If user is scrolling up
    if (currentScrollTop < lastScrollTop.current) {
      shouldAutoScroll.current = false;
      setIsUserScrolling(true);
    }
    // If user is at bottom
    else if (isAtBottom) {
      shouldAutoScroll.current = true;
      setIsUserScrolling(false);
    }

    // Update last scroll position
    lastScrollTop.current = currentScrollTop;

    // Set a timeout to reset user scrolling state after 1 second of no scrolling
    scrollTimeout.current = setTimeout(() => {
      if (isAtBottom) {
        shouldAutoScroll.current = true;
        setIsUserScrolling(false);
      }
    }, 1000);
  }, []);

  // Auto-scroll when messages update or during streaming
  useEffect(() => {
    if (shouldAutoScroll.current && messagesContainerRef.current) {
      const { scrollHeight, scrollTop, clientHeight } = messagesContainerRef.current;
      const isAtBottom = scrollHeight - scrollTop - clientHeight <= 50;

      if (isAtBottom) {
        scrollToBottom();
      }
    }
  }, [messages, scrollToBottom]);

  // Add scroll event listener with passive option for better performance
  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    if (messagesContainer) {
      messagesContainer.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        messagesContainer.removeEventListener('scroll', handleScroll);
        if (scrollTimeout.current) {
          clearTimeout(scrollTimeout.current);
        }
      };
    }
  }, [handleScroll]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, []);

  // Update currentConversation when conversation prop changes
  useEffect(() => {
    setCurrentConversation(conversation);
    // Set ready for chat if it's not a shared conversation
    if (!isShared) {
      setIsReadyForChat(true);
    }
  }, [conversation, isShared]);

  useEffect(() => {
    if (currentConversation && currentConversation.id) {
      const conversationId = currentConversation.conversation_id || currentConversation.id;
      
      // Use forked conversation ID if available
      const targetConversationId = forkedConversationId || conversationId;

      // Check if we already have messages for this conversation to avoid unnecessary fetching
      const isNewConversation = currentConversationId !== targetConversationId;
      const hasNoMessages = messages.length === 0;

      // Fetch messages if it's a new conversation or if we don't have any messages
      if (isNewConversation || hasNoMessages) {
        // If conversation already has chat_history, use it directly for faster loading
        if (currentConversation.chat_history && Array.isArray(currentConversation.chat_history) && currentConversation.chat_history.length > 0) {
          const formattedMessages = currentConversation.chat_history.flatMap(entry => {
            const messageArray = [];

            if (entry.user) {
              messageArray.push({
                id: `user-${entry.timestamp}`,
                content: entry.user,
                sender: 'user',
                timestamp: entry.timestamp,
                image_url: entry.user_image || null
              });
            }

            if (entry.ai) {
              messageArray.push({
                id: `ai-${entry.timestamp}`,
                content: entry.ai,
                sender: 'ai',
                timestamp: entry.timestamp,
                function_used: entry.function_used,
                related_images: entry.related_images || []
              });
            }

            return messageArray;
          });
          setMessages(formattedMessages);
        } else {
          // Fallback: fetch conversation messages from API
          fetchConversationMessages(targetConversationId);
        }
      }
    }
    return () => {
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, [currentConversation, currentConversationId, messages.length, forkedConversationId]);

  const fetchConversationMessages = async (conversationId) => {
    // Only show loading state if we don't have any messages yet
    if (messages.length === 0) {
      setIsLoading(true);
    }
    setError(null);

    try {
      // Using the POST method since conversation_id needs to be in the body
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.GET_CHAT}`;

      const requestBody = {
        conversation_id: conversationId
      };
      // console.log('Request body for GET_CHAT:', requestBody);

      // Import the API client dynamically to avoid circular dependency
      const { apiClient } = await import('./utils/apiClient');

      const response = await apiClient.post(url, requestBody);

      if (!response.ok) {
        throw new Error(`Failed to fetch messages: ${response.status}`);
      }

      const data = await response.json();
      // console.log('Chat history data:', data);

      // Transform the chat_history into the format expected by the component
      if (data.chat_history && Array.isArray(data.chat_history)) {
        const formattedMessages = data.chat_history.flatMap(entry => {
          const messages = [];

          if (entry.user) {
            messages.push({
              id: `user-${entry.timestamp}`,
              content: entry.user,
              sender: 'user',
              timestamp: entry.timestamp,
              image_url: entry.user_image || null // Use user_image field from the response
            });
          }

          if (entry.ai) {
            messages.push({
              id: `ai-${entry.timestamp}`,
              content: entry.ai,
              sender: 'ai',
              timestamp: entry.timestamp,
              function_used: entry.function_used,
              related_images: entry.related_images || []
            });
          }

          return messages;
        });

        setMessages(formattedMessages);
      } else {
        setMessages([]);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching conversation messages:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploading(true);
    try {
      setUploadedFile({
        file_name: file.name,
        url: URL.createObjectURL(file),
        file,
        type: 'image'
      });
    } catch (error) {
      alert('Error processing file');
    } finally {
      setIsUploading(false);
    }
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && !uploadedFile) || !currentConversation) return;

    const conversationId = currentConversation.conversation_id || currentConversation.id;
    const authToken = localStorage.getItem('authToken');

    if (!authToken) {
      setError('Authentication token missing. Please log in again.');
      return;
    }

    // console.log("Sending message with conversation_id:", conversationId);

    // Add user message to UI immediately
    const userMessage = {
      id: `user-${Date.now()}`,
      content: newMessage,
      sender: 'user',
      timestamp: new Date().toISOString(),
      image_url: uploadedFile?.public_url || null
    };

    setMessages(prevMessages => [...prevMessages, userMessage]);
    setNewMessage('');

    // Store uploaded file info and clear the state
    const fileInfo = uploadedFile;
    setUploadedFile(null);

    // Create a temp AI message for UI feedback
    const tempAiMessageId = `ai-${Date.now()}`;
    setMessages(prevMessages => [...prevMessages, {
      id: tempAiMessageId,
      content: '',
      sender: 'ai',
      timestamp: new Date().toISOString(),
      isTemp: true
    }]);

    shouldScrollRef.current = true;
    scrollToBottom();
    setIsStreaming(true);

    try {
      let endpoint = `${API_CONFIG.BASE_URL}/backend-api/conversations/send`;

      // If there's an image, use the image endpoint instead
      if (fileInfo) {
        endpoint = `${API_CONFIG.BASE_URL}/backend-api/conversations/image`;
      }

      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();

      // Prepare request body
      const requestBody = {
        query: newMessage,
        conversation_id: conversationId
      };

      // Add image information if present
      if (fileInfo) {
        requestBody.image_url = fileInfo.public_url;
        requestBody.file_name = fileInfo.file_name;
        requestBody.type = fileInfo.type;
      }

      // Import the API client dynamically to avoid circular dependency
      const { apiClient } = await import('./utils/apiClient');

      const response = await apiClient.request(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody),
        signal: abortControllerRef.current.signal
      });

      // Handle non-ok responses
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail?.[0]?.msg || 'Failed to send message');
      }

      // Process SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedContent = '';
      let productImages = [];
      let functionCallData = null;
      let searchStatusData = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              // console.log('Received data:', data);

              switch (data.type) {
                case 'chunk':
                  // Update accumulated content
                  accumulatedContent += data.content;

                  // If this is the first chunk with actual content, clear function call and search status
                  if (accumulatedContent.trim()) {
                    functionCallData = null;
                    searchStatusData = null;
                  }

                  // Update the AI message
                  setMessages(prevMessages =>
                    prevMessages.map(msg =>
                      msg.id === tempAiMessageId
                        ? {
                          ...msg,
                          content: accumulatedContent,
                          isTemp: false,
                          functionCall: accumulatedContent.trim() ? null : functionCallData,
                          searchStatus: accumulatedContent.trim() ? null : searchStatusData
                        }
                        : msg
                    )
                  );
                  break;

                case 'images':
                case 'csv_images':
                case 'pdf_images':
                  // Store images for display
                  console.log('🖼️ ConversationView received images:', data.type, data);
                  
                  if (data.images && Array.isArray(data.images)) {
                    // Process and normalize incoming images
                    const newImages = data.images.map((img, idx) => ({
                      url: img.url || img.base64 || img.src,
                      name: img.name || img.description || `Generated visualization ${idx + 1}`,
                      id: img.id || `img-${Date.now()}-${idx}`,
                      isGraph: data.type === 'csv_images' || data.type === 'pdf_images'
                    }));
                    
                    // Merge with existing images (avoid duplicates)
                    const existingUrls = new Set(productImages.map(img => img.url));
                    const uniqueNewImages = newImages.filter(img => !existingUrls.has(img.url));
                    productImages = [...productImages, ...uniqueNewImages];
                    
                    console.log('🖼️ Total images:', productImages.length);
                    console.log('🖼️ Images:', productImages.map(img => ({
                      url: img.url?.substring(0, 50) + '...',
                      name: img.name
                    })));
                  }

                  // Update the AI message with images
                  setMessages(prevMessages =>
                    prevMessages.map(msg =>
                      msg.id === tempAiMessageId
                        ? {
                          ...msg,
                          // Store the full image objects instead of just URLs
                          related_images: productImages,
                          isTemp: false
                        }
                        : msg
                    )
                  );
                  break;

                case 'function_call_started':
                  // Update function call data
                  functionCallData = {
                    state: 'started',
                    name: data.functionName,
                    arguments: ''
                  };

                  // Update the message with function call info
                  setMessages(prevMessages =>
                    prevMessages.map(msg =>
                      msg.id === tempAiMessageId
                        ? {
                          ...msg,
                          content: accumulatedContent,
                          functionCall: functionCallData,
                          searchStatus: searchStatusData,
                          isTemp: false
                        }
                        : msg
                    )
                  );
                  break;

                case 'function_call_arguments':
                  // Update function call data with arguments
                  if (functionCallData) {
                    functionCallData = {
                      ...functionCallData,
                      state: 'arguments',
                      arguments: (functionCallData.arguments || '') + (data.functionArguments || '')
                    };
                  }

                  // Update the message with function call info
                  setMessages(prevMessages =>
                    prevMessages.map(msg =>
                      msg.id === tempAiMessageId
                        ? {
                          ...msg,
                          content: accumulatedContent,
                          functionCall: functionCallData,
                          searchStatus: searchStatusData,
                          isTemp: false
                        }
                        : msg
                    )
                  );
                  break;

                case 'function_execution_started':
                  // Update function call data to executing state
                  if (functionCallData) {
                    functionCallData = {
                      ...functionCallData,
                      state: 'executing'
                    };
                  }

                  // Update the message with function call info
                  setMessages(prevMessages =>
                    prevMessages.map(msg =>
                      msg.id === tempAiMessageId
                        ? {
                          ...msg,
                          content: accumulatedContent,
                          functionCall: functionCallData,
                          searchStatus: searchStatusData,
                          isTemp: false
                        }
                        : msg
                    )
                  );
                  break;

                case 'function_execution_completed':
                  // Update function call data to completed state
                  if (functionCallData) {
                    functionCallData = {
                      ...functionCallData,
                      state: 'completed'
                    };
                  }

                  // Update the message with function call info
                  setMessages(prevMessages =>
                    prevMessages.map(msg =>
                      msg.id === tempAiMessageId
                        ? {
                          ...msg,
                          content: accumulatedContent,
                          functionCall: functionCallData,
                          searchStatus: searchStatusData,
                          isTemp: false
                        }
                        : msg
                    )
                  );
                  break;

                case 'knowledge_base_search_started':
                  // Update search status data
                  searchStatusData = {
                    state: 'searching'
                  };

                  // Update the message with search status
                  setMessages(prevMessages =>
                    prevMessages.map(msg =>
                      msg.id === tempAiMessageId
                        ? {
                          ...msg,
                          content: accumulatedContent,
                          functionCall: functionCallData,
                          searchStatus: searchStatusData,
                          isTemp: false
                        }
                        : msg
                    )
                  );
                  break;

                case 'knowledge_base_search_completed':
                  // Update search status data to completed
                  searchStatusData = {
                    state: 'completed'
                  };

                  // Update the message with search status
                  setMessages(prevMessages =>
                    prevMessages.map(msg =>
                      msg.id === tempAiMessageId
                        ? {
                          ...msg,
                          content: accumulatedContent,
                          functionCall: functionCallData,
                          searchStatus: searchStatusData,
                          isTemp: false
                        }
                        : msg
                    )
                  );
                  break;

                case 'error':
                  throw new Error(data.error);
              }

              // Set flag to scroll to bottom after each update
              shouldScrollRef.current = true;
              scrollToBottom();
            } catch (e) {
              console.error('Error parsing SSE data:', e, 'Raw line:', line);
            }
          }
        }
      }

      // If stream completed without error but we have empty content
      if (!accumulatedContent) {
        setMessages(prevMessages =>
          prevMessages.filter(msg => msg.id !== tempAiMessageId)
        );
        throw new Error('No response received from server');
      }

      // Re-fetch conversation messages to ensure we have the latest state
      fetchConversationMessages(conversationId);

    } catch (error) {
      if (error.name === 'AbortError') {
        // console.log('Request was aborted');
        // Don't replace content when a request is aborted, just mark as no longer temporary
        setMessages(prevMessages =>
          prevMessages.map(msg =>
            msg.id === tempAiMessageId
              ? {
                ...msg,
                isTemp: false
              }
              : msg
          )
        );
      } else {
        console.error('Error sending message:', error);
        setMessages(prevMessages =>
          prevMessages.map(msg =>
            msg.id === tempAiMessageId
              ? {
                id: `error-${Date.now()}`,
                content: `Error: ${error.message || 'Failed to send message. Please try again.'}`,
                sender: 'error',
                timestamp: new Date().toISOString(),
                failed: true
              }
              : msg
          )
        );
      }
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  // Helper to extract user ID from JWT token
  const getUserIdFromToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.user_id;
    } catch (error) {
      console.error('Error extracting user ID from token:', error);
      return null;
    }
  };

  // FunctionCallStatus component for displaying streaming status messages
  const FunctionCallStatus = ({ functionCall, searchStatus }) => {
    let statusText = '';

    // Determine the text content based on status type
    if (searchStatus && searchStatus.state === 'searching') {
      statusText = 'Searching knowledge base...';
    } else if (searchStatus && searchStatus.state === 'completed') {
      statusText = 'Search completed';
    } else if (functionCall) {
      if (functionCall.state === 'started') {
        statusText = 'Processing your request...';
      } else if (functionCall.state === 'arguments') {
        statusText = 'Analyzing information...';
      } else if (functionCall.state === 'executing') {
        statusText = 'Finding information...';
      } else if (functionCall.state === 'completed') {
        statusText = 'Processing complete';
      }
    }

    if (!statusText) return null;

    return (
      <div className="assistant-text" style={{ textAlign: 'left', padding: 0, margin: 0, display: 'block' }}>
        <em>{statusText}</em>
      </div>
    );
  };

  const handleMicClick = async () => {
    setIsMicModalOpen(true);

    if (isListening) {
      setIsListening(false);
      setIsMicModalOpen(false);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
        audioStreamRef.current = null;
      }
      if (webSocketRef.current?.readyState === WebSocket.OPEN) {
        try {
          webSocketRef.current.send(JSON.stringify({ type: 'input_audio_buffer.commit' }));
          webSocketRef.current.close();
        } catch (error) {
          console.error('Error closing WebSocket:', error);
        }
        webSocketRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      return;
    }
    try {
      setTranscriptionText('');
      const authToken = localStorage.getItem('authToken');
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }
      let audioBufferQueue = [];
      let isPlaying = false;
      const decodeAndPlayAudio = (base64Audio) => {
        try {
          const cleanBase64 = base64Audio.replace(/\s+/g, '');
          if (!/^[A-Za-z0-9+/=]+$/.test(cleanBase64)) {
            throw new Error('Invalid base64 string');
          }
          const binaryString = atob(cleanBase64);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const audioData = new Int16Array(bytes.buffer);
          const audioBuffer = audioContextRef.current.createBuffer(1, audioData.length, audioContextRef.current.sampleRate);
          const floatData = new Float32Array(audioData.length);
          for (let i = 0; i < audioData.length; i++) {
            floatData[i] = audioData[i] / 32768;
          }
          audioBuffer.copyToChannel(floatData, 0);
          audioBufferQueue.push(audioBuffer);
          if (!isPlaying) {
            playNextBuffer();
          }
        } catch (error) {
          console.error('Error decoding audio:', error);
        }
      };
      const playNextBuffer = () => {
        if (audioBufferQueue.length === 0) {
          isPlaying = false;
          return;
        }
        isPlaying = true;
        const buffer = audioBufferQueue.shift();
        if (!audioContextRef.current) return;
        const source = audioContextRef.current.createBufferSource();
        source.buffer = buffer;
        source.connect(audioContextRef.current.destination);
        source.onended = () => {
          playNextBuffer();
        };
        source.start(0);
      };
      const resampleAudio = (inputData, inputSampleRate) => {
        return new Promise((resolve) => {
          const offlineContext = new OfflineAudioContext(1, Math.ceil((inputData.length * 24000) / inputSampleRate), 24000);
          const buffer = offlineContext.createBuffer(1, inputData.length, inputSampleRate);
          buffer.copyToChannel(inputData, 0);
          const source = offlineContext.createBufferSource();
          source.buffer = buffer;
          source.connect(offlineContext.destination);
          source.start();
          offlineContext.startRendering().then((renderedBuffer) => {
            const resampledData = renderedBuffer.getChannelData(0);
            resolve(resampledData);
          });
        });
      };
      const base64EncodeAudio = (floatArray) => {
        const buffer = new ArrayBuffer(floatArray.length * 2);
        const view = new DataView(buffer);
        for (let i = 0; i < floatArray.length; i++) {
          const s = Math.max(-1, Math.min(1, floatArray[i]));
          view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
        }
        const binary = String.fromCharCode.apply(null, new Uint8Array(buffer));
        return btoa(binary);
      };
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 24000,
          sampleSize: 16,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: false
        }
      });
      audioStreamRef.current = stream;
      let wsUrl = `ws://${API_CONFIG.BASE_URL.replace('http://', '')}/backend-api/conversations/audio`;
      if (authToken) {
        wsUrl += `?token=${encodeURIComponent(authToken)}`;
      }
      const ws = new WebSocket(wsUrl);
      ws.binaryType = 'arraybuffer';
      const connectionTimeoutId = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          setIsListening(false);
          setIsMicModalOpen(false);
          if (audioStreamRef.current) {
            audioStreamRef.current.getTracks().forEach(track => track.stop());
            audioStreamRef.current = null;
          }
          if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
          }
        }
      }, 10000);
      const pingInterval = setInterval(() => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          try {
            ws.send(JSON.stringify({ type: 'ping' }));
          } catch (err) {
            console.error('Error sending ping:', err);
          }
        } else {
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
          }
        }
      }, 10000);
      pingIntervalRef.current = pingInterval;
      ws.onopen = () => {
        clearTimeout(connectionTimeoutId);
        if (authToken) {
          try {
            ws.send(JSON.stringify({ type: 'authenticate', token: authToken }));
          } catch (error) {
            console.error('Error sending authentication message:', error);
          }
        }
        setIsListening(true);
        const sourceNode = audioContextRef.current.createMediaStreamSource(stream);
        const processorNode = audioContextRef.current.createScriptProcessor(4096, 1, 1);
        sourceNode.connect(processorNode);
        processorNode.connect(audioContextRef.current.destination);
        processorNode.onaudioprocess = async (e) => {
          if (webSocketRef.current?.readyState === WebSocket.OPEN) {
            try {
              const inputData = e.inputBuffer.getChannelData(0);
              const inputSampleRate = e.inputBuffer.sampleRate;
              const resampledData = await resampleAudio(inputData, inputSampleRate);
              const base64Chunk = base64EncodeAudio(resampledData);
              webSocketRef.current.send(base64Chunk);
            } catch (error) {
              console.error('Error processing audio:', error);
            }
          }
        };
      };
      ws.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);
          switch (data.type) {
            case 'authenticated':
              break;
            case 'authentication_failed':
              break;
            case 'clear_audio_queue':
              audioBufferQueue = [];
              isPlaying = false;
              break;
            case 'audio_response':
              if (data.audio) {
                decodeAndPlayAudio(data.audio);
              }
              break;
            case 'error':
              break;
            case 'transcription':
              setTranscriptionText(data.text);
              break;
            case 'conversation_stopped':
              setIsListening(false);
              break;
            case 'response.canceled':
              audioBufferQueue = [];
              isPlaying = false;
              break;
            case 'pong':
              break;
            default:
              break;
          }
        } catch (error) {
          console.error('Error processing WebSocket message:', error);
        }
      };
      ws.onerror = (error) => {
        setIsListening(false);
        setIsMicModalOpen(false);
      };
      ws.onclose = (event) => {
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        if (isListening) {
          setIsListening(false);
          setIsMicModalOpen(false);
          if (audioStreamRef.current) {
            audioStreamRef.current.getTracks().forEach(track => track.stop());
            audioStreamRef.current = null;
          }
          if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
          }
        }
      };
      webSocketRef.current = ws;
    } catch (error) {
      setIsListening(false);
      setIsMicModalOpen(false);
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
        audioStreamRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    }
  };

  const handleCloseMicModal = () => {
    setIsMicModalOpen(false);
    if (isListening) {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current = null;
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
        audioStreamRef.current = null;
      }
      if (webSocketRef.current) {
        try {
          if (webSocketRef.current.readyState === WebSocket.OPEN) {
            webSocketRef.current.send(JSON.stringify({ type: 'client_disconnect' }));
            webSocketRef.current.close();
          }
        } catch (err) {
          console.error('Error while closing WebSocket:', err);
        }
        webSocketRef.current = null;
      }
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch (err) {
          console.error('Error while closing AudioContext:', err);
        }
        audioContextRef.current = null;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      setIsListening(false);
      setTranscriptionText('');
    }
  };

  const handleStopGeneration = async () => {
    // console.log('Handling stop generation');
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        setIsStreaming(false);
        setIsLoading(false);
        // Instead of removing the temp message, mark it as no longer temporary
        // This preserves any content that was already streamed
        setMessages(prevMessages =>
          prevMessages.map(msg =>
            msg.isTemp
              ? { ...msg, isTemp: false }
              : msg
          )
        );

        // console.log('Message generation was stopped');
      }

      // Send cancellation request to backend
      if (currentConversation && currentConversation.conversation_id) {
        const conversationId = currentConversation.conversation_id || currentConversation.id;
        const authToken = localStorage.getItem('authToken');

        if (authToken) {
          try {
            const userId = getUserIdFromToken(authToken);
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.CHAT}`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                conversation_id: conversationId,
                user_id: userId,
                cancel: true, // Flag to indicate cancellation
              }),
            });

            if (!response.ok) {
              console.error('Failed to send cancellation request:', response.statusText);
            } else {
              // console.log('Cancellation request sent successfully for conversation:', conversationId);
            }
          } catch (err) {
            console.error('Error sending cancellation request:', err);
          }
        } else {
          console.warn('No auth token available, skipping cancellation request');
        }
      }

      // Clear ping interval if exists
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    } catch (err) {
      console.error('Error handling stop generation:', err);
    }
  };

  const startEditing = (message) => {
    setEditingMessageId(message.id);
    setEditValue(message.content);
  };

  const cancelEditing = () => {
    setEditingMessageId(null);
    setEditValue('');
  };

  const submitEdit = async (messageId) => {
    if (!editValue.trim()) {
      cancelEditing();
      return;
    }

    const editedMessage = messages.find(msg => msg.id === messageId);
    if (!editedMessage) {
      cancelEditing();
      return;
    }

    // Update the message content in UI
    setMessages(prevMessages =>
      prevMessages.map(msg =>
        msg.id === messageId
          ? { ...msg, content: editValue }
          : msg
      )
    );

    // Find AI responses that follow this message
    const messageIndex = messages.findIndex(msg => msg.id === messageId);
    if (messageIndex >= 0 && messageIndex < messages.length - 1 && messages[messageIndex + 1].sender === 'ai') {
      // Remove the AI response that came after this message
      setMessages(prevMessages =>
        prevMessages.filter(msg => msg.id !== messages[messageIndex + 1].id)
      );
    }

    // Clean up edit state
    cancelEditing();

    // Now resend the edited message as a new request
    await sendEditedMessage(editedMessage, editValue);
  };

  const sendEditedMessage = async (originalMessage, editedContent) => {
    if (!editedContent.trim() || !currentConversation) return;

    const conversationId = currentConversation.conversation_id || currentConversation.id;
    const authToken = localStorage.getItem('authToken');

    if (!authToken) {
      setError('Authentication token missing. Please log in again.');
      return;
    }

    // console.log("Sending edited message with conversation_id:", conversationId);

    // Create a temp AI message for UI feedback
    const tempAiMessageId = `ai-${Date.now()}`;
    setMessages(prevMessages => [...prevMessages, {
      id: tempAiMessageId,
      content: '',
      sender: 'ai',
      timestamp: new Date().toISOString(),
      isTemp: true
    }]);

    shouldScrollRef.current = true;
    scrollToBottom();
    setIsStreaming(true);

    try {
      let endpoint = `${API_CONFIG.BASE_URL}/backend-api/conversations/send`;

      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();

      // Prepare request body
      const requestBody = {
        query: editedContent,
        conversation_id: conversationId
      };

      // Import the API client dynamically to avoid circular dependency
      const { apiClient } = await import('./utils/apiClient');

      const response = await apiClient.request(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody),
        signal: abortControllerRef.current.signal
      });

      // Handle non-ok responses
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail?.[0]?.msg || 'Failed to send message');
      }

      // Process SSE stream - reuse the existing stream handling logic
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedContent = '';
      let productImages = [];
      let functionCallData = null;
      let searchStatusData = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              // console.log('Received data:', data);

              // Reuse existing switch logic for handling different response types
              switch (data.type) {
                case 'chunk':
                  accumulatedContent += data.content;
                  if (accumulatedContent.trim()) {
                    functionCallData = null;
                    searchStatusData = null;
                  }
                  setMessages(prevMessages =>
                    prevMessages.map(msg =>
                      msg.id === tempAiMessageId
                        ? {
                          ...msg,
                          content: accumulatedContent,
                          isTemp: false,
                          functionCall: accumulatedContent.trim() ? null : functionCallData,
                          searchStatus: accumulatedContent.trim() ? null : searchStatusData
                        }
                        : msg
                    )
                  );
                  break;

                // ... existing case handlers for other data types
                case 'images':
                case 'csv_images':
                case 'pdf_images':
                  console.log('🖼️ ConversationView (handler 2) received images:', data.type, data);
                  
                  if (data.images && Array.isArray(data.images)) {
                    // Process and normalize incoming images
                    const newImages = data.images.map((img, idx) => ({
                      url: img.url || img.base64 || img.src,
                      name: img.name || img.description || `Generated visualization ${idx + 1}`,
                      id: img.id || `img-${Date.now()}-${idx}`,
                      isGraph: data.type === 'csv_images' || data.type === 'pdf_images'
                    }));
                    
                    // Merge with existing images (avoid duplicates)
                    const existingUrls = new Set(productImages.map(img => img.url));
                    const uniqueNewImages = newImages.filter(img => !existingUrls.has(img.url));
                    productImages = [...productImages, ...uniqueNewImages];
                    
                    console.log('🖼️ Total images (handler 2):', productImages.length);
                    console.log('🖼️ Images (handler 2):', productImages.map(img => ({
                      url: img.url?.substring(0, 50) + '...',
                      name: img.name
                    })));
                  }
                  
                  setMessages(prevMessages =>
                    prevMessages.map(msg =>
                      msg.id === tempAiMessageId
                        ? {
                          ...msg,
                          related_images: productImages,
                          isTemp: false
                        }
                        : msg
                    )
                  );
                  break;

                case 'function_call_started':
                  functionCallData = {
                    state: 'started',
                    name: data.functionName,
                    arguments: ''
                  };
                  setMessages(prevMessages =>
                    prevMessages.map(msg =>
                      msg.id === tempAiMessageId
                        ? {
                          ...msg,
                          content: accumulatedContent,
                          functionCall: functionCallData,
                          searchStatus: searchStatusData,
                          isTemp: false
                        }
                        : msg
                    )
                  );
                  break;

                case 'function_call_arguments':
                  if (functionCallData) {
                    functionCallData = {
                      ...functionCallData,
                      state: 'arguments',
                      arguments: (functionCallData.arguments || '') + (data.functionArguments || '')
                    };
                  }
                  setMessages(prevMessages =>
                    prevMessages.map(msg =>
                      msg.id === tempAiMessageId
                        ? {
                          ...msg,
                          content: accumulatedContent,
                          functionCall: functionCallData,
                          searchStatus: searchStatusData,
                          isTemp: false
                        }
                        : msg
                    )
                  );
                  break;

                case 'function_execution_started':
                  if (functionCallData) {
                    functionCallData = {
                      ...functionCallData,
                      state: 'executing'
                    };
                  }
                  setMessages(prevMessages =>
                    prevMessages.map(msg =>
                      msg.id === tempAiMessageId
                        ? {
                          ...msg,
                          content: accumulatedContent,
                          functionCall: functionCallData,
                          searchStatus: searchStatusData,
                          isTemp: false
                        }
                        : msg
                    )
                  );
                  break;

                case 'function_execution_completed':
                  if (functionCallData) {
                    functionCallData = {
                      ...functionCallData,
                      state: 'completed'
                    };
                  }
                  setMessages(prevMessages =>
                    prevMessages.map(msg =>
                      msg.id === tempAiMessageId
                        ? {
                          ...msg,
                          content: accumulatedContent,
                          functionCall: functionCallData,
                          searchStatus: searchStatusData,
                          isTemp: false
                        }
                        : msg
                    )
                  );
                  break;

                case 'knowledge_base_search_started':
                  searchStatusData = {
                    state: 'searching'
                  };
                  setMessages(prevMessages =>
                    prevMessages.map(msg =>
                      msg.id === tempAiMessageId
                        ? {
                          ...msg,
                          content: accumulatedContent,
                          functionCall: functionCallData,
                          searchStatus: searchStatusData,
                          isTemp: false
                        }
                        : msg
                    )
                  );
                  break;

                case 'knowledge_base_search_completed':
                  searchStatusData = {
                    state: 'completed'
                  };
                  setMessages(prevMessages =>
                    prevMessages.map(msg =>
                      msg.id === tempAiMessageId
                        ? {
                          ...msg,
                          content: accumulatedContent,
                          functionCall: functionCallData,
                          searchStatus: searchStatusData,
                          isTemp: false
                        }
                        : msg
                    )
                  );
                  break;

                case 'error':
                  throw new Error(data.error);
              }

              shouldScrollRef.current = true;
              scrollToBottom();
            } catch (e) {
              console.error('Error parsing SSE data:', e, 'Raw line:', line);
            }
          }
        }
      }

      // If stream completed without error but we have empty content
      if (!accumulatedContent) {
        setMessages(prevMessages =>
          prevMessages.filter(msg => msg.id !== tempAiMessageId)
        );
        throw new Error('No response received from server');
      }

      // Re-fetch conversation messages to ensure we have the latest state
      fetchConversationMessages(conversationId);

    } catch (error) {
      // Reuse existing error handling
      if (error.name === 'AbortError') {
        // console.log('Request was aborted');
        setMessages(prevMessages =>
          prevMessages.map(msg =>
            msg.id === tempAiMessageId
              ? {
                ...msg,
                isTemp: false
              }
              : msg
          )
        );
      } else {
        console.error('Error sending edited message:', error);
        setMessages(prevMessages =>
          prevMessages.map(msg =>
            msg.id === tempAiMessageId
              ? {
                id: `error-${Date.now()}`,
                content: `Error: ${error.message || 'Failed to send message. Please try again.'}`,
                sender: 'error',
                timestamp: new Date().toISOString(),
                failed: true
              }
              : msg
          )
        );
      }
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  // Reset scroll state when starting new chat
  const handleNewChatClick = () => {
    shouldAutoScroll.current = true;
    setIsUserScrolling(false);
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }
    // ... rest of existing handleNewChatClick code ...
  };

  // Add useEffect for dynamic viewport height calculation
  useEffect(() => {
    // First we get the viewport height and multiply it by 1% to get a value for a vh unit
    const setViewportHeight = () => {
      let vh = window.innerHeight * 0.01;
      // Then we set the value in the --vh custom property to the root of the document
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // Set initial value
    setViewportHeight();

    // We listen to the resize event to update the vh value when needed
    window.addEventListener('resize', setViewportHeight);

    // Clean up event listener
    return () => {
      window.removeEventListener('resize', setViewportHeight);
    };
  }, []);

  // Add event listener to detect sidebar state and add class to body
  useEffect(() => {
    const handleSidebarToggle = (e) => {
      if (e.detail && e.detail.isOpen !== undefined) {
        if (e.detail.isOpen) {
          document.body.classList.add('sidebar-open');
        } else {
          document.body.classList.remove('sidebar-open');
        }
      }
    };

    window.addEventListener('sidebar-toggle', handleSidebarToggle);

    return () => {
      window.removeEventListener('sidebar-toggle', handleSidebarToggle);
    };
  }, []);

  // Copy handler for assistant messages
  const handleCopy = (message) => {
    if (!message.content) return;
    // If content is an object, stringify, else use as is
    const text = typeof message.content === 'string' ? message.content : JSON.stringify(message.content);
    navigator.clipboard.writeText(text).then(() => {
      setCopiedMessageId(message.id);
      setTimeout(() => setCopiedMessageId(null), 1500);
    });
  };

  // URL handling is now managed by React Router in the parent App component
  // No need to manually manipulate the URL here

  // Add event listeners for navigation events
  useEffect(() => {
    const handleNavigateToConversation = (event) => {
      if (event.detail && event.detail.conversationId) {
        setMessages([]);
      }
    };

    const handleClearConversation = () => {
      if (currentConversation && currentConversation.id) {
        const conversationId = currentConversation.conversation_id || currentConversation.id;
        // Update URL with conversation ID
        // window.history.pushState({}, '', `/c/${conversationId}`);
      }
    };

    // Handle browser back button
    const handlePopState = () => {
      // Clear the conversation when going back
      setMessages([]);
      setNewMessage('');
      setUploadedFile(null);
      setIsLoading(false);
      setError(null);
      setIsStreaming(false);
    };

    window.addEventListener('navigateToConversation', handleNavigateToConversation);
    window.addEventListener('clearConversation', handleClearConversation);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('navigateToConversation', handleNavigateToConversation);
      window.removeEventListener('clearConversation', handleClearConversation);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [currentConversation]);

  // Handle back navigation
  const handleBackClick = () => {
    // Clear the URL
    navigate('/');
    // Clear the conversation
    setMessages([]);
    // Trigger parent component to show main view
    window.dispatchEvent(new CustomEvent('clearConversation'));
  };

  // Add handler for feedback button
  const handleOpenFeedback = () => {
    window.open('https://forms.office.com/r/gp2dd1xEA5?origin=lprLink', '_blank');
  };

  // Function to clean conversation title by removing UUIDs
  const getCleanTitle = (conversation) => {
    if (!conversation) return 'Conversation';
    console.log('conversation', conversation);
    let title = conversation.title || 'Conversation';
    
    // If title is too long, truncate it
    if (title.length > 50) {
      title = title.substring(0, 50) + '...';
    }
    
    return title;
  };

  // Function to render user message content with preserved line breaks
  const renderUserMessageContent = (content) => {
    if (typeof content === 'object') {
      if (content.text) {
        return content.text.split('\n').map((line, index) => (
          <React.Fragment key={index}>
            {line}
            {index < content.text.split('\n').length - 1 && <br />}
          </React.Fragment>
        ));
      }
      return content;
    } else if (typeof content === 'string') {
      return content.split('\n').map((line, index) => (
        <React.Fragment key={index}>
          {line}
          {index < content.split('\n').length - 1 && <br />}
        </React.Fragment>
      ));
    }
    return content;
  };

  // Add this function before the return statement
  const renderMessageContent = (content) => {
    if (!content) return null;

    // Check if content contains math expressions (single $ or double $$)
    const hasMath = content.includes('$') || content.includes('$$');

    if (hasMath) {
      return (
        <MarkdownWithMath
          content={content}
          remarkPlugins={[remarkGfm, remarkMath]}
          rehypePlugins={[rehypeKatex]}
          components={{
            h1: ({ node, ...props }) => <h1 style={{ fontSize: '1.5rem', fontWeight: '600', margin: '1.2rem 0 0.7rem', color: '#111', lineHeight: '1.3', textAlign: 'left' }} {...props} />,
            h2: ({ node, ...props }) => <h2 style={{ fontSize: '1.3rem', fontWeight: '600', margin: '1.2rem 0 0.7rem', color: '#222', lineHeight: '1.3', textAlign: 'left' }} {...props} />,
            h3: ({ node, ...props }) => <h3 style={{ fontSize: '1.2rem', fontWeight: '600', margin: '1rem 0 0.6rem', color: '#222', lineHeight: '1.3', textAlign: 'left' }} {...props} />,
            h4: ({ node, ...props }) => <h4 style={{ fontSize: '1.1rem', fontWeight: '600', margin: '1rem 0 0.6rem', color: '#333', lineHeight: '1.3', textAlign: 'left' }} {...props} />,
            p: ({ node, ...props }) => <p style={{ margin: '0.6rem 0', lineHeight: '1.6', textAlign: 'left' }} {...props} />,
            ul: ({ node, ...props }) => <ul style={{ paddingLeft: '1.8rem', margin: '0.7rem 0', textAlign: 'left' }} {...props} />,
            ol: ({ node, ...props }) => <ol style={{ paddingLeft: '1.8rem', margin: '0.7rem 0', textAlign: 'left' }} {...props} />,
            li: ({ node, ...props }) => <li style={{ marginBottom: '0.5rem', textAlign: 'left' }} {...props} />,
            blockquote: ({ node, ...props }) => <blockquote style={{ borderLeft: '3px solid #ccc', marginLeft: '0', marginRight: '0', paddingLeft: '1rem', color: '#555', fontStyle: 'italic' }} {...props} />,
            table: ({ node, ...props }) => (
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', margin: '1rem 0' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.9rem', minWidth: '500px' }} {...props} />
              </div>
            ),
            th: ({ node, ...props }) => <th style={{ border: '1px solid #ddd', padding: '0.5rem', backgroundColor: '#f1f1f1', fontWeight: '600', textAlign: 'left' }} {...props} />,
            td: ({ node, ...props }) => <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }} {...props} />,
            a: ({ node, href, ...props }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#2241dd', textDecoration: 'underline' }}
                {...props}
              />
            ),
          }}
        />
      );
    }

    // For regular content without math
    return (
      <MarkdownWithMath
        content={content}
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ node, ...props }) => <h1 style={{ fontSize: '1.5rem', fontWeight: '600', margin: '1.2rem 0 0.7rem', color: '#111', lineHeight: '1.3', textAlign: 'left' }} {...props} />,
          h2: ({ node, ...props }) => <h2 style={{ fontSize: '1.3rem', fontWeight: '600', margin: '1.2rem 0 0.7rem', color: '#222', lineHeight: '1.3', textAlign: 'left' }} {...props} />,
          h3: ({ node, ...props }) => <h3 style={{ fontSize: '1.2rem', fontWeight: '600', margin: '1rem 0 0.6rem', color: '#222', lineHeight: '1.3', textAlign: 'left' }} {...props} />,
          h4: ({ node, ...props }) => <h4 style={{ fontSize: '1.1rem', fontWeight: '600', margin: '1rem 0 0.6rem', color: '#333', lineHeight: '1.3', textAlign: 'left' }} {...props} />,
          p: ({ node, ...props }) => <p style={{ margin: '0.6rem 0', lineHeight: '1.6', textAlign: 'left' }} {...props} />,
          ul: ({ node, ...props }) => <ul style={{ paddingLeft: '1.8rem', margin: '0.7rem 0', textAlign: 'left' }} {...props} />,
          ol: ({ node, ...props }) => <ol style={{ paddingLeft: '1.8rem', margin: '0.7rem 0', textAlign: 'left' }} {...props} />,
          li: ({ node, ...props }) => <li style={{ marginBottom: '0.5rem', textAlign: 'left' }} {...props} />,
          blockquote: ({ node, ...props }) => <blockquote style={{ borderLeft: '3px solid #ccc', marginLeft: '0', marginRight: '0', paddingLeft: '1rem', color: '#555', fontStyle: 'italic' }} {...props} />,
          table: ({ node, ...props }) => (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', margin: '1rem 0' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.9rem', minWidth: '500px' }} {...props} />
            </div>
          ),
          th: ({ node, ...props }) => <th style={{ border: '1px solid #ddd', padding: '0.5rem', backgroundColor: '#f1f1f1', fontWeight: '600', textAlign: 'left' }} {...props} />,
          td: ({ node, ...props }) => <td style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }} {...props} />,
          a: ({ node, href, ...props }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#2241dd', textDecoration: 'underline' }}
              {...props}
            />
          ),
        }}
      />
    );
  };

  // Clear all state when conversation changes or becomes null
  useEffect(() => {
    const newConversationId = currentConversation ? (currentConversation.conversation_id || currentConversation.id) : null;

    // Only clear state if we're switching to a different conversation or no conversation
    if (currentConversationId !== newConversationId) {
      if (!currentConversation) {
        // Only clear everything if there's truly no conversation
        setMessages([]);
        setNewMessage('');
        setUploadedFile(null);
        setIsLoading(false);
        setError(null);
        setIsStreaming(false);
        setEditingMessageId(null);
        setEditValue('');
        setPreviewImage(null);
        setPreviewPos({ x: 0, y: 0 });
        setCopiedMessageId(null);
        setIsUploading(false);
        setIsMicModalOpen(false);
        setIsListening(false);
        setTranscriptionText('');
        setFile(null);
      } else if (currentConversationId && currentConversationId !== newConversationId) {
        // Clear messages only when switching to a different conversation
        setMessages([]);
        setError(null);
        setIsLoading(false);
        setIsStreaming(false);
      }

      setCurrentConversationId(newConversationId);
    }
  }, [currentConversation, currentConversationId]);

  const handleStartChat = async () => {
    if (!currentConversation || isForking) return;

    const conversationId = currentConversation.conversation_id || currentConversation.id;
    const authToken = localStorage.getItem('authToken');

    if (!authToken) {
      setError('Authentication token missing. Please log in again.');
      return;
    }

    try {
      setIsForking(true);
      setIsLoading(true);
      
      // Import the API client dynamically to avoid circular dependency
      const { apiClient } = await import('./utils/apiClient');
      
      // Fork the shared conversation (without sending a message)
      const forkResponse = await apiClient.post(`${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.FORK_SHARED}/${conversationId}/fork`, {
        query: '' // Empty query to just fork without sending a message
      });

      if (!forkResponse.ok) {
        const errorData = await forkResponse.json();
        throw new Error(errorData.detail?.[0]?.msg || 'Failed to fork shared conversation');
      }

      const forkData = await forkResponse.json();
      const newConversationId = forkData.conversation_id;
      
      setForkedConversationId(newConversationId);
      setIsForked(true);
      
      // Update the conversation object with the new ID
      currentConversation.conversation_id = newConversationId;
      currentConversation.id = newConversationId;
      
      // Update the current conversation state
      const updatedConversation = {
        ...currentConversation,
        id: newConversationId,
        conversation_id: newConversationId,
        title: forkData.title
      };
      setCurrentConversation(updatedConversation);
      
      // Navigate to the new conversation URL
      navigate(`/c/${newConversationId}`);
      
      // Dispatch event to update sidebar
      window.dispatchEvent(new CustomEvent('conversationForked', {
        detail: { 
          oldConversationId: conversationId,
          newConversationId: newConversationId,
          title: forkData.title
        }
      }));
      
      // Update the current conversation ID state
      setCurrentConversationId(newConversationId);
      
      // Clear existing messages and re-fetch with the new conversation ID
      setMessages([]);
      await fetchConversationMessages(newConversationId);
      
      // Mark as ready for chat
      setIsReadyForChat(true);
      
      // Focus on the input box after a short delay
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
      
    } catch (error) {
      console.error('Error forking shared conversation:', error);
      setError('Failed to start conversation. Please try again.');
    } finally {
      setIsLoading(false);
      setIsForking(false);
    }
  };

  // Add handler for feedback button
  // const handleOpenFeedback = () => {
  //   window.open('https://forms.office.com/r/gp2dd1xEA5?origin=lprLink', '_blank');
  // };

  // Handle login for shared conversations
  const handleLogin = () => {
    // Navigate to login page
    navigate('/Login');
  };

  // Handle close login modal
  const handleCloseLoginModal = () => {
    setShowLoginModal(false);
    // Navigate back to home if user doesn't want to login
    navigate('/');
  };

  if (!currentConversation) {
    // Return null to completely hide the conversation container
    // This ensures the conversation header and messages container are not rendered
    return null;
  }

  return (
    <MathJaxContext version={3.2} config={config}>
      <div className="conversation-container">
        <div className="conversation-header">
          <div className="logo-container">
            {/* Space reserved for logo */}
          </div>
          {/* <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h2>{getCleanTitle(currentConversation)}</h2>
            {isShared && (
              <span
                style={{
                  background: '#007bff',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '500',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title="This is a shared conversation"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3"></circle>
                  <circle cx="6" cy="12" r="3"></circle>
                  <circle cx="18" cy="19" r="3"></circle>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                </svg>
                Shared
              </span>
            )}
          </div> */}
          <button
            className="feedback-btn"
            onClick={handleOpenFeedback}
            title="Feedback"
            style={{
              position: 'absolute',
              top: '15px',
              right: '20px',
              zIndex: 1,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              color: '#2241dd',
              fontWeight: '500',
              fontSize: '14px'
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              <path d="M8 9h8"></path>
              <path d="M8 13h6"></path>
            </svg>
            Feedback
          </button>
        </div>

        <div className="messages-container" ref={messagesContainerRef} onScroll={handleScroll}>
          {isLoading && messages.length === 0 ? (
            <div className="loading-messages">Loading messages...</div>
          ) : error ? (
            <div className="error-message">Error: {error}</div>
          ) : messages.length === 0 ? (
            <div className="no-messages">No messages in this conversation yet.</div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`message-block ${message.sender}`}
                style={{ position: 'relative' }}
              >
                {message.sender === 'user' ? (
                  <div className="user-message">
                    {editingMessageId === message.id ? (
                      <div className="edit-container">
                        <textarea
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="edit-textarea"
                          autoFocus
                        />
                        <div className="edit-actions">
                          <button
                            onClick={() => submitEdit(message.id)}
                            className="edit-save-button"
                            disabled={!editValue.trim()}
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="edit-cancel-button"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <h3 className="user-heading">
                          <span className="user-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                              <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                          </span>
                          {renderUserMessageContent(message.content)}
                          <button
                            className="edit-button"
                            onClick={() => startEditing(message)}
                            title="Edit message"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 20h9"></path>
                              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                            </svg>
                          </button>
                        </h3>

                        {/* Display user uploaded image if any */}
                        {message.image_url && (
                          <div className="user-image-container" style={{
                            marginTop: '8px',
                            marginLeft: '32px',
                            maxWidth: '300px'
                          }}>
                            <img
                              src={message.image_url}
                              alt="User shared image"
                              style={{
                                maxWidth: '100%',
                                borderRadius: '8px',
                                border: '1px solid #e0e0e0',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                              }}
                              onClick={() => window.open(message.image_url, '_blank')}
                              onMouseOver={(e) => {
                                const rect = e.target.getBoundingClientRect();
                                setPreviewPos({
                                  x: rect.left + window.scrollX + rect.width / 2,
                                  y: rect.top + window.scrollY + rect.height + 5
                                });
                                setPreviewImage(message.image_url);
                              }}
                              onMouseOut={() => {
                                setPreviewImage(null);
                              }}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : message.sender === 'ai' ? (
                  <div className="assistant-message" style={{ position: 'relative' }}>
                    {/* Copy icon button for text/markdown responses only */}
                    {message.content && (
                      <button
                        className="copy-btn copy-btn-mobile"
                        onClick={() => handleCopy(message)}
                        title="Copy response"
                        style={{
                          position: 'absolute',
                          top: 8,
                          left: 0,
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 2,
                          zIndex: 2,
                          opacity: 0.7,
                          transition: 'opacity 0.2s',
                        }}
                        onMouseOver={e => (e.currentTarget.style.opacity = '1')}
                        onMouseOut={e => (e.currentTarget.style.opacity = '0.7')}
                      >
                        {Icons.copy}
                        {copiedMessageId === message.id && (
                          <span style={{
                            position: 'absolute',
                            top: '-22px',
                            left: '-10px',
                            background: '#2241DD',
                            color: 'white',
                            borderRadius: '4px',
                            padding: '2px 8px',
                            fontSize: '12px',
                            fontWeight: 500,
                            boxShadow: '0 2px 8px rgba(34,65,221,0.08)',
                            zIndex: 10,
                            whiteSpace: 'nowrap',
                          }}>
                            Copied!
                          </span>
                        )}
                      </button>
                    )}
                    <div className="assistant-content">
                      {/* Show function call status instead of typing dots if available - but only if no content */}
                      {!message.content && (message.functionCall || message.searchStatus) && (
                        <FunctionCallStatus
                          functionCall={message.functionCall}
                          searchStatus={message.searchStatus}
                        />
                      )}

                      {/* Only show content if we have actual content */}
                      {message.content && (
                        <>
                          {/* Process the content using our renderMessageContent function */}
                          {renderMessageContent(processContent(message.content))}
                        </>
                      )}

                      {/* Display related images if any */}
                      {message.related_images && message.related_images.length > 0 && (
                        <div className="assistant-images">
                          {message.related_images.map((image, index) => {
                            // Handle both object format and string format
                            const isObjectFormat = typeof image === 'object' && image !== null;
                            const imageUrl = isObjectFormat ? image.url : image;

                            // Extract caption from appropriate source
                            let caption = '';

                            if (isObjectFormat && image.name) {
                              // Use the name property from the image object
                              caption = image.name;
                            } else {
                              // Fallback to extracting from URL
                              try {
                                const urlParts = new URL(imageUrl).pathname.split('/');
                                const filename = urlParts[urlParts.length - 1];
                                caption = filename.split('.')[0].replace(/[_-]/g, ' ');
                                // Capitalize first letter of each word
                                caption = caption.split(' ')
                                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                  .join(' ');
                              } catch (e) {
                                caption = `Related Image ${index + 1}`;
                              }
                            }

                            return (
                              <div key={index} className="image-item">
                                <img
                                  src={imageUrl}
                                  alt={caption}
                                  className="assistant-image"
                                  onClick={() => window.open(imageUrl, '_blank')}
                                  onMouseOver={(e) => {
                                    const rect = e.target.getBoundingClientRect();
                                    setPreviewPos({
                                      x: rect.left + window.scrollX + rect.width / 2,
                                      y: rect.top + window.scrollY + rect.height + 5
                                    });
                                    setPreviewImage(imageUrl);
                                  }}
                                  onMouseOut={() => {
                                    setPreviewImage(null);
                                  }}
                                />
                                <div className="image-caption">{caption}</div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Show loading indicator only if there's no function call or search status */}
                      {message.isTemp && !message.functionCall && !message.searchStatus && (
                        <div className="typing-dots-container">
                          <div className="typing-dot" style={{ animationDelay: '-0.32s' }}></div>
                          <div className="typing-dot" style={{ animationDelay: '-0.16s' }}></div>
                          <div className="typing-dot" style={{ animationDelay: '0s' }}></div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  // Error message
                  <div className="error-message">
                    <div className="error-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                    </div>
                    <div className="error-content">
                      {message.content}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Start Chat Button for Shared Conversations */}
        {isShared && !isReadyForChat && (
          <div style={{
            display: 'none', // Hide the old button
            justifyContent: 'center',
            alignItems: 'center',
            padding: '40px 20px',
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            borderTop: '1px solid #e9ecef',
            marginTop: 'auto'
          }}>
            <div style={{
              textAlign: 'center',
              maxWidth: '400px'
            }}>
              <div style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#333',
                marginBottom: '12px'
              }}>
                Ready to continue this conversation?
              </div>
              <div style={{
                fontSize: '14px',
                color: '#666',
                marginBottom: '24px',
                lineHeight: '1.5'
              }}>
                Click "Start Chat" to create your own copy of this conversation and begin chatting.
              </div>
              <button
                onClick={handleStartChat}
                disabled={isForking}
                style={{
                  background: '#2241DD',
                  color: 'white',
                  border: 'none',
                  borderRadius: '24px',
                  padding: '12px 32px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: isForking ? 'not-allowed' : 'pointer',
                  opacity: isForking ? 0.7 : 1,
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 12px rgba(34, 65, 221, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  margin: '0 auto'
                }}
                onMouseOver={(e) => {
                  if (!isForking) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(34, 65, 221, 0.4)';
                  }
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(34, 65, 221, 0.3)';
                }}
              >
                {isForking ? (
                  <>
                    <svg style={{ animation: 'spin 1s linear infinite' }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 12a9 9 0 11-6.219-8.56" />
                    </svg>
                    Starting Chat...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      <path d="M8 9h8" />
                      <path d="M8 13h6" />
                    </svg>
                    Start Chat
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        <div className="input-area">
          {isShared && !isReadyForChat ? (
            // Start Chat Button for Shared Conversations - styled like input box
            <div className="textarea-container" style={{ 
              position: 'relative',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '48px',
              height: '48px',
              border: '1.5px solid #3b82f6',
              borderRadius: '24px',
              padding: '0 16px',
              boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
              background: '#fff',
              transition: 'border 0.2s'
            }}>
              <button
                onClick={handleStartChat}
                disabled={isForking}
                style={{
                  background: 'transparent',
                  color: '#2241DD',
                  border: 'none',
                  borderRadius: '24px',
                  padding: '12px 32px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: isForking ? 'not-allowed' : 'pointer',
                  opacity: isForking ? 0.7 : 1,
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  justifyContent: 'center'
                }}
                onMouseOver={(e) => {
                  if (!isForking) {
                    e.target.style.background = '#f8f9fa';
                  }
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'transparent';
                }}
              >
                {isForking ? (
                  <>
                    <svg style={{ animation: 'spin 1s linear infinite' }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 12a9 9 0 11-6.219-8.56" />
                    </svg>
                    Starting Chat...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      <path d="M8 9h8" />
                      <path d="M8 13h6" />
                    </svg>
                    Click to Start Chat
                  </>
                )}
              </button>
            </div>
          ) : (
            // Normal input form for ready conversations
            <form className="message-form" onSubmit={(e) => { e.preventDefault(); sendMessage(); }}>
              <div className="textarea-container" style={{ 
                position: 'relative',
                opacity: isReadyForChat ? 1 : 0.6,
                pointerEvents: isReadyForChat ? 'auto' : 'none'
              }} onClick={() => document.getElementById("userMessageInput").focus()}>
                {/* <button
                  type="button"
                  className="mic-btn"
                  onClick={handleMicClick}
                  title={isListening ? "Stop recording" : "Voice input"}
                  disabled={!isReadyForChat}
                  style={{
                    marginRight: "8px",
                    borderRadius: "50%",
                    padding: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: isListening ? "#ff4b4b" : "#f5f5f5",
                    border: "none",
                    transition: "background-color 0.3s ease",
                    cursor: isReadyForChat ? "pointer" : "not-allowed",
                    alignSelf: "center",
                    opacity: isReadyForChat ? 1 : 0.5,
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={isListening ? "white" : "currentColor"}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" y1="19" x2="12" y2="22" />
                  </svg>
                </button> */}
                <textarea
                  id="userMessageInput"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder={isReadyForChat ? "Type your message here..." : "Click 'Start Chat' to begin..."}
                  disabled={isLoading || isStreaming || !isReadyForChat}
                  rows="1"
                  ref={inputRef}
                />
                <div className="input-buttons">
                  <input
                    type="file"
                    id="fileUpload"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                    accept="image/*"
                    disabled={isStreaming}
                  />
                  <button
                    type="button"
                    className="upload-btn"
                    onClick={() => document.getElementById("fileUpload").click()}
                    disabled={isLoading || isUploading || isStreaming || !isReadyForChat}
                    title="Attach file"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-6 h-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.122 2.122l7.81-7.81"
                      />
                    </svg>
                  </button>
                  {(isLoading || isStreaming) ? (
                    <button
                      type="button"
                      className="stop-btn"
                      onClick={handleStopGeneration}
                      title="Stop generating"
                      style={{ padding: 0, background: 'none', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <svg width="32" height="32" viewBox="0 0 32 32">
                        <circle cx="16" cy="16" r="16" fill="#2241DD" />
                        <rect x="10" y="10" width="12" height="12" rx="2" fill="white" />
                      </svg>
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="send-btn"
                      disabled={!newMessage.trim() || isLoading || !isReadyForChat}
                      style={{ padding: 0, background: 'none', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <svg width="32" height="32" viewBox="0 0 32 32">
                        <g>
                          <line x1="16" y1="8" x2="16" y2="24" stroke="#2241DD" strokeWidth="2" strokeLinecap="round" />
                          <polyline points="10,14 16,8 22,14" fill="none" stroke="#2241DD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </g>
                      </svg>
                    </button>
                  )}
                </div>
                {uploadedFile && (
                  <div className="image-preview-card">
                    <img src={uploadedFile.url} alt="Uploaded" />
                    <div className="image-preview-info">
                      <div className="image-preview-filename">{uploadedFile.file_name}</div>
                      <div className="image-preview-type">Image</div>
                    </div>
                    <button
                      className="image-preview-remove"
                      onClick={() => setUploadedFile(null)}
                      title="Remove image"
                    >×</button>
                  </div>
                )}
              </div>
            </form>
          )}
          <MicrophoneModal
            isOpen={isMicModalOpen}
            onClose={handleCloseMicModal}
            isListening={isListening}
            transcriptionText={transcriptionText}
          />
        </div>

        {previewImage && (
          <div
            className="image-preview-popup"
            style={{
              position: 'fixed',
              left: `${previewPos.x}px`,
              top: `${previewPos.y}px`,
              transform: 'translate(-50%, 10px)',
              zIndex: 9999,
              pointerEvents: 'none',
              boxShadow: '0 5px 20px rgba(0,0,0,0.25)',
              borderRadius: '8px',
              backgroundColor: 'white',
              padding: '5px',
              backdropFilter: 'blur(4px)',
              border: '1px solid rgba(255,255,255,0.8)'
            }}
          >
            <img
              src={previewImage}
              alt="Preview"
              style={{
                maxWidth: '240px',
                maxHeight: '180px',
                borderRadius: '6px',
                border: '1px solid #eee'
              }}
            />
          </div>
        )}

        {/* Disclosure text */}
        <div className="disclaimer-text">
          Help us improve GEOHUB by sharing your feedback on its responses.
          By messaging GEOHUB, you acknowledge it is an AI that can make mistakes and agree to our <a href="https://www.encardio.com/privacy-policies" target="_blank" rel="noopener noreferrer">Terms and Privacy Policy</a>.
        </div>

        <style jsx>{`
          .conversation-container {
            display: flex;
            flex-direction: column;
            height: 100vh;
            height: calc(var(--vh, 1vh) * 100); /* Dynamic height for mobile */
            background-color: #fff;
            overflow: hidden;
            position: relative; /* Add position relative */
            width: 100%; /* Ensure container takes full width */
          }
          
          // .conversation-header {
          //   padding: 15px 20px;
          //   border-bottom: 1px solid #eaeaea;
          //   margin-bottom: 10px;
          //   flex-shrink: 0;
          //   display: flex;
          //   flex-direction: column; /* Stack logo and title vertically */
          //   align-items: center;
          //   justify-content: center;
          //   background-color: #fff;
          //   position: sticky;
          //   top: 0;
          //   left: 0;
          //   right: 0;
          //   z-index: 10;
          //   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          //   height: auto;
            
          // }
          
          .logo-container {
            width: 100%;
            height: 50px; /* Fixed height for logo area */
            margin-bottom: 10px; /* Space between logo and title */
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            display: flex;
            justify-content: center;
            align-items: center;
          }
          
          .conversation-header h2 {
            margin: 0;
            font-size: 1.5rem;
            color: #2241dd;
            font-weight: 600;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 90%;
            padding-top: 5px; /* Additional padding at the top */
          }
          
          /* Feedback button styles */
          .feedback-btn {
            transition: color 0.2s ease, transform 0.1s ease;
          }
          
          .feedback-btn:hover {
            color: #1a33b0 !important;
            transform: translateY(-1px);
          }
          
          .feedback-btn:active {
            transform: translateY(0);
          }
          
          /* Tablet view */
          @media (max-width: 768px) {
            .conversation-header {
              padding: 12px 16px;
              margin-bottom: 8px;
            }
            
            .logo-container {
              height: 45px; /* Slightly smaller on tablets */
              margin-bottom: 8px;
            }
            
            .conversation-header h2 {
              font-size: 1.3rem;
              max-width: 85%;
            }
            
            .feedback-btn {
              top: 12px !important;
              right: 16px !important;
              font-size: 13px !important;
            }
            
            .feedback-btn svg {
              width: 18px !important;
              height: 18px !important;
            }
          }
          
          /* Mobile view */
          @media (max-width: 480px) {
            .conversation-header {
              padding: 8px 16px 12px; /* Adjusted padding */
              margin-bottom: 6px;
              height: auto;
            }
            
            .logo-container {
              height: 40px; /* Smaller on mobile */
              margin-bottom: 5px;
            }
            
            .conversation-header h2 {
              font-size: 1.1rem;
              max-width: 90%;
              text-align: center;
              margin: 0 auto;
            }
            
            .feedback-btn {
              top: 10px !important;
              right: 12px !important;
              font-size: 12px !important;
            }
            
            .feedback-btn svg {
              width: 16px !important;
              height: 16px !important;
            }
          }
          
          /* Small mobile view */
          @media (max-width: 360px) {
            .conversation-header {
              padding: 6px 10px 8px;
              padding-top: 45px; /* Less padding on very small screens */
            }
            
            .logo-container {
              height: 35px;
              margin-bottom: 4px;
            }
            
            .conversation-header h2 {
              font-size: 1rem;
              max-width: 95%;
              margin-top: 5px; /* Add a little margin to separate from logo */
            }
            
            .feedback-btn {
              top: 8px !important;
              right: 8px !important;
              font-size: 11px !important;
              gap: 3px !important;
            }
            
            .feedback-btn svg {
              width: 14px !important;
              height: 14px !important;
            }
            
            body.sidebar-open .conversation-container,
            body.sidebar-open .input-area {
              width: calc(100% - 180px); /* Even smaller sidebar adjustment for tiny screens */
            }
          }
          
          .messages-container {
            flex: 1;
            overflow-y: auto;
            padding: 0 20px 80px; /* Add bottom padding to prevent content being hidden behind input */
            scroll-behavior: smooth;
            -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
          }

          /* Message Block Styles */
          .message-block {
            margin-bottom: 1.5rem;
            width: 100%;
          }

          /* User Message as Heading */
          .user-message {
            margin-bottom: 0.5rem;
          }

          .user-heading {
            font-size: 1.1rem;
            font-weight: 600;
            color: #2241DD;
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 0 0 8px 0;
            border-bottom: 1px solid #e1e4e8;
            margin: 0;
          }

          .user-icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
            background-color: #2241DD;
            border-radius: 50%;
            padding: 4px;
          }

          .user-icon svg {
            color: white;
          }

          /* Assistant Message */
          .assistant-message {
            padding-left: 32px;
          }

          .assistant-content {
            font-size: 0.95rem;
            line-height: 1.6;
            color: #333;
          }
          
          /* Assistant Images */
          .assistant-images {
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
            margin: 12px 0;
          }
          
          .image-item {
            position: relative;
            flex: 0 0 auto;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            transition: transform 0.2s ease, box-shadow 0.2s ease;
          }
          
          .image-item:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          }
          
          .assistant-image {
            width: 100%;
            max-height: 200px;
            object-fit: cover;
            cursor: pointer;
          }
          
          .image-caption {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            background: rgba(0,0,0,0.6);
            color: white;
            padding: 4px 8px;
            font-size: 0.8rem;
            text-align: center;
          }

          /* Error Message */
          .error-message {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding: 12px;
            background-color: #FFEBEE;
            border-radius: 8px;
            margin: 0.5rem 0;
          }

          .error-icon {
            min-width: 24px;
            height: 24px;
            background-color: #f44336;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
          }

          .error-content {
            color: #C62828;
            font-size: 0.9rem;
            line-height: 1.5;
          }

          /* Typing Loader */
          .typing-dots-container {
            display: flex;
            gap: 4px;
            margin-top: 8px;
            padding-left: 2px;
          }

          .typing-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background-color: #2241DD;
            opacity: 0.6;
            display: inline-block;
            animation: bounce 1.4s infinite ease-in-out both;
          }

          @keyframes bounce {
            0%, 80%, 100% {
              transform: scale(0);
            }
            40% {
              transform: scale(1);
            }
          }
          
          /* Typing cursor for simulating streaming text */
          .typing-cursor {
            display: inline-block;
            font-weight: 400;
            color: #444;
            animation: blink 1s infinite;
            position: relative;
            margin-left: 1px;
          }
          
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }
          
          /* Style for text-only function status messages */
          .assistant-text {
            font-size: 16px;
            color: #444;
            margin: 8px
            line-height: 1.5;
            text-align: left;
            display: block;
            animation: fadeIn 0.2s ease;
          }
          
          .assistant-text em {
            font-style: italic;
            color: #444;
            font-weight: normal;
          }
          
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(5px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          .loading-messages,
          .no-messages {
            text-align: center;
            padding: 20px;
            color: #777;
            font-style: italic;
          }
          
          .image-gallery {
            margin-top: 15px;
            width: 100%;
          }
          
          .image-category {
            font-size: 0.85rem;
            font-weight: 600;
            color: #555;
            margin-bottom: 8px;
            padding-left: 5px;
            border-left: 3px solid #2241DD;
          }
          
          .message-images {
            display: flex;
            flex-wrap: nowrap;
            gap: 10px;
            margin: 5px 0;
            overflow-x: auto;
            padding-bottom: 8px;
            -ms-overflow-style: none;
            scrollbar-width: thin;
          }
          
          .message-images::-webkit-scrollbar {
            height: 4px;
          }
          
          .message-images::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 10px;
          }
          
          .message-images::-webkit-scrollbar-thumb {
            background: #ccc;
            border-radius: 10px;
          }
          
          .image-item {
            position: relative;
            flex: 0 0 auto;
          }
          
          .related-image {
            max-width: 120px;
            max-height: 120px;
            border-radius: 6px;
            cursor: pointer;
            object-fit: cover;
            border: 1px solid #eee;
            transition: transform 0.2s ease;
          }
          
          .related-image:hover {
            transform: translateY(-2px);
          }
          
          .image-number {
            position: absolute;
            top: 5px;
            right: 5px;
            background-color: rgba(0,0,0,0.6);
            color: white;
            font-size: 0.7rem;
            padding: 2px 6px;
            border-radius: 10px;
          }

          .input-area {
            flex: 0 0 auto;
            padding: 10px 20px 20px;
            width: 67%;
            display: flex;
            align-items: flex-end;
            background-color: #fff;
            margin-left: 17.5%;
            bottom: 0;
            bottom: env(safe-area-inset-bottom, 0); /* Support for devices with safe areas */
            z-index: 37; /* Ensure it's above other content */
            max-width: 100vw; /* Ensure it doesn't extend beyond viewport width */
            transition: width 0.3s ease-in-out, left 0.3s ease-in-out; /* Smooth transition when sidebar opens/closes */
          }
          
          /* Add styles to handle sidebar */
          body.sidebar-open .input-area {
            width: calc(100% - 250px); /* Adjust width when sidebar is open */
            left: 250px; /* Shift to right by sidebar width */
          }
          
          @media (max-width: 768px) {
            body.sidebar-open .conversation-container {
              width: calc(100% - 250px);
              margin-left: 250px;
            }
            
            body.sidebar-open .input-area {
              width: calc(100% - 250px); /* Adjust based on sidebar width */
              left: 250px;
            }
            
            .input-area {
              padding: 10px 12px 12px;
            }
            
            .messages-container {
              padding: 0 12px 80px; /* Adjust side padding */
            }
          }
          
          @media (max-width: 480px) {
            .conversation-header {
              padding-top: 50px; /* Less padding on mobile */
            }
            
            .logo-container {
              height: 40px;
            }
            
            .input-area {
              padding: 8px 10px 10px;
            }
            
            .messages-container {
              padding: 0 8px 70px; /* Further reduce padding */
            }
            
            .assistant-message {
              padding-left: 16px; /* Reduce padding on mobile */
            }
            
            /* Ensure input area doesn't overlap with sidebar on mobile */
            body.sidebar-open .conversation-container {
              width: calc(100% - 200px);
              margin-left: 200px;
            }
            
            body.sidebar-open .input-area {
              width: calc(100% - 200px); /* Smaller sidebar width on mobile */
              left: 200px;
            }
          }
          
          /* Additional style for very small screens */
          @media (max-width: 360px) {
            body.sidebar-open .conversation-container {
              width: calc(100% - 180px);
              margin-left: 180px;
            }
            
            body.sidebar-open .input-area {
              width: calc(100% - 180px);
              left: 180px;
            }
          }
          
          .message-form {
            width: 100%;
          }
          
          .textarea-container {
            position: relative;
            display: flex;
            align-items: center;
            border-radius: 24px;
            border: 1.5px solid #3b82f6;
            padding: 0 16px;
            box-shadow: 0 2px 5px rgba(0,0,0,0.05);
            cursor: text;
            min-height: 48px;
            height: 48px;
            background: #fff;
            width: 100%;
            transition: border 0.2s;
          }
          
          .textarea-container textarea {
            min-height: 32px;
            max-height: 48px;
            flex: 1 1 auto;
            background: transparent;
            z-index: 1;
            border: none;
            outline: none;
            font-size: 1rem;
            padding: 12px 0;
            resize: none;
            font-family: inherit;
          }
          
          .textarea-container .input-buttons {
            z-index: 2;
            display: flex;
            gap: 8px;
          }
          
          .textarea-container .image-preview-card {
            position: absolute;
            top: 50%;
            right: 12px;
            transform: translateY(-50%);
            width: 120px;
            height: 40px;
            background: #fff;
            border-radius: 10px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.10);
            border: 1px solid #eee;
            display: flex;
            flex-direction: row;
            align-items: center;
            padding: 4px 8px 4px 4px;
            overflow: hidden;
            z-index: 10;
          }
          
          .textarea-container .image-preview-card img {
            width: 32px;
            height: 32px;
            object-fit: cover;
            border-radius: 7px;
            margin-right: 6px;
          }
          
          .textarea-container .image-preview-card .image-preview-info {
            flex: 1 1 auto;
            display: flex;
            flex-direction: column;
            justify-content: center;
            min-width: 0;
          }
          
          .textarea-container .image-preview-card .image-preview-filename {
            font-size: 11px;
            color: #333;
            word-break: break-all;
            line-height: 1.1;
            max-width: 60px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          
          .textarea-container .image-preview-card .image-preview-type {
            font-size: 10px;
            color: #888;
            line-height: 1.1;
          }
          
          .textarea-container .image-preview-card .image-preview-remove {
            background: rgba(0,0,0,0.6);
            color: #fff;
            border: none;
            border-radius: 50%;
            width: 18px;
            height: 18px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            font-size: 13px;
            margin-left: 4px;
            z-index: 11;
          }
          
          /* Adjustments for small screens */
          @media (max-width: 600px) {
            .textarea-container textarea {
              padding: 10px 0;
              font-size: 15px;
            }
            
            .textarea-container {
              padding: 0 12px;
              min-height: 44px;
              height: 44px;
            }
            
            .textarea-container .image-preview-card {
              width: 80px;
              height: 32px;
            }
            
            .textarea-container .image-preview-card img {
              width: 22px;
              height: 22px;
            }
            
            .textarea-container .image-preview-card .image-preview-filename {
              max-width: 30px;
              font-size: 9px;
            }
            
            .mic-btn {
              margin-left: 0;
              padding: 6px;
            }
            
            .mic-btn svg {
              width: 18px;
              height: 18px;
            }
          }

          .mic-btn {
            background: none;
            border: none;
            cursor: pointer;
            padding: 8px;
            margin-left: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .mic-btn svg {
            width: 20px;
            height: 20px;
          }
          
          .mic-btn:disabled {
            color: #ccc;
            cursor: not-allowed;
          }

          .image-preview-popup {
            animation: fadeIn 0.2s ease;
            background-color: white;
            padding: 5px;
            box-shadow: 0 5px 20px rgba(0,0,0,0.25);
            border-radius: 8px;
            backdrop-filter: blur(4px);
            border: 1px solid rgba(255,255,255,0.8);
          }

          @media (max-width: 768px) {
            .messages-container {
              padding: 0 12px 80px; /* Reduce side padding on mobile */
              overflow-x: hidden; /* Prevent horizontal scrolling on mobile */
              width: 100%;
              box-sizing: border-box;
            }
            
            /* Mobile-specific text wrapping */
            .messages-container * {
              max-width: 100%;
              box-sizing: border-box;
            }

            .messages-container p,
            .messages-container div,
            .messages-container span,
            .messages-container h1,
            .messages-container h2,
            .messages-container h3,
            .messages-container h4,
            .messages-container h5,
            .messages-container h6,
            .messages-container li,
            .messages-container td,
            .messages-container th {
              overflow-wrap: break-word;
              word-wrap: break-word;
              word-break: break-word;
              hyphens: auto;
            }
            
            .message-block {
              margin-bottom: 1.25rem;
              padding-right: 0; /* Remove any right padding on mobile */
              max-width: 100%;
              overflow-wrap: break-word;
              word-wrap: break-word;
              box-sizing: border-box;
            }
            
            .assistant-message {
              padding-left: 24px;
            }
            
            .user-heading {
              font-size: 1rem;
              flex-wrap: wrap; /* Allow wrapping on mobile */
              max-width: 100%;
              overflow-wrap: break-word;
              word-wrap: break-word;
              box-sizing: border-box;
            }
            
            .assistant-content {
              font-size: 0.9rem;
              max-width: 100%;
              overflow-wrap: break-word;
              word-wrap: break-word;
              word-break: break-word;
              hyphens: auto;
              box-sizing: border-box;
            }
            
            .conversation-container {
              padding: 0;
              max-width: 100vw;
              overflow-x: hidden;
              box-sizing: border-box;
            }
          }

          @media (max-width: 576px) {
            .messages-container {
              padding: 0 8px 70px; /* Further reduce padding on small screens */
              overflow-x: hidden;
              width: 100%;
              box-sizing: border-box;
            }
            
            /* Enhanced mobile text wrapping for small screens */
            .messages-container * {
              max-width: 100%;
              box-sizing: border-box;
            }

            .messages-container p,
            .messages-container div,
            .messages-container span,
            .messages-container h1,
            .messages-container h2,
            .messages-container h3,
            .messages-container h4,
            .messages-container h5,
            .messages-container h6,
            .messages-container li,
            .messages-container td,
            .messages-container th {
              overflow-wrap: break-word;
              word-wrap: break-word;
              word-break: break-word;
              hyphens: auto;
            }
            
            .message-block {
              margin-bottom: 1.25rem;
              padding-right: 0;
              max-width: 100%;
              overflow-wrap: break-word;
              word-wrap: break-word;
              box-sizing: border-box;
            }
            
            .user-heading {
              font-size: 0.95rem;
              flex-wrap: wrap;
              max-width: 100%;
              overflow-wrap: break-word;
              word-wrap: break-word;
              box-sizing: border-box;
            }
            
            .assistant-message {
              padding-left: 16px; /* Reduce left padding on small screens */
            }
            
            .assistant-content {
              font-size: 0.85rem;
              max-width: 100%;
              overflow-wrap: break-word;
              word-wrap: break-word;
              word-break: break-word;
              hyphens: auto;
              box-sizing: border-box;
            }
          }

          .input-area .textarea-container {
            position: relative;
          }
          .input-area .textarea-container > div[style*='absolute'] {
            box-shadow: 0 2px 8px rgba(0,0,0,0.12);
            transition: box-shadow 0.2s;
          }
          .input-area .textarea-container > div[style*='absolute']:hover {
            box-shadow: 0 4px 16px rgba(0,0,0,0.18);
          }

          .stop-btn {
            background: none;
            border: none;
            cursor: pointer;
            padding: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .stop-btn svg {
            width: 20px;
            height: 20px;
          }

          .stop-btn:disabled {
            color: #ccc;
            cursor: not-allowed;
          }

          /* Add styles for edit functionality */
          .edit-button {
            display: inline-flex;
            background: none;
            border: none;
            cursor: pointer;
            padding: 4px;
            margin-left: 8px;
            color: #999;
            transition: color 0.2s;
          }
          
          .edit-button:hover {
            color: #2241DD;
          }
          
          .edit-container {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .edit-textarea {
            width: 100%;
            min-height: 60px;
            padding: 12px;
            border: 2px solid #2241DD;
            border-radius: 8px;
            font-family: inherit;
            font-size: 1rem;
            resize: vertical;
            margin-bottom: 8px;
          }
          
          .edit-actions {
            display: flex;
            gap: 8px;
            justify-content: flex-end;
          }
          
          .edit-save-button, .edit-cancel-button {
            padding: 6px 12px;
            border-radius: 4px;
            font-size: 0.9rem;
            cursor: pointer;
            transition: all 0.2s;
          }
          
          .edit-save-button {
            background-color: #2241DD;
            color: white;
            border: none;
          }
          
          .edit-save-button:hover {
            background-color: #1a33b0;
          }
          
          .edit-save-button:disabled {
            background-color: #a0a0a0;
            cursor: not-allowed;
          }
          
          .edit-cancel-button {
            background-color: #f5f5f5;
            color: #666;
            border: 1px solid #ddd;
          }
          
          .edit-cancel-button:hover {
            background-color: #e5e5e5;
          }

          /* --- Responsive Fix for Mobile Input Area and Textarea --- */
          @media (max-width: 768px) {
            .input-area {
              width: 100% !important;
              margin-left: 0 !important;
              padding: 10px 8px 12px !important;
              left: 0 !important;
              max-width: 100vw !important;
            }
            .textarea-container {
              width: 100% !important;
              min-width: 0 !important;
              padding: 0 8px !important;
            }
          }
          @media (max-width: 480px) {
            .input-area {
              width: 100% !important;
              margin-left: 0 !important;
              padding: 8px 4px 10px !important;
              left: 0 !important;
              max-width: 100vw !important;
            }
            .textarea-container {
              width: 100% !important;
              min-width: 0 !important;
              padding: 0 4px !important;
            }
          }
          /* --- End Responsive Fix --- */

          /* Disclaimer text styling */
          .disclaimer-text {
            text-align: center;
            color: #666;
            font-size: 12px;
            line-height: 1.4;
            padding: 8px 16px;
            margin-top: 8px;
            margin-bottom: 16px;
          }
          
          .disclaimer-text a {
            color: #2241DD;
            text-decoration: underline;
          }
          
          @media (max-width: 768px) {
            .disclaimer-text {
              font-size: 11px;
              padding: 6px 12px;
            }
          }
          
          @media (max-width: 480px) {
            .disclaimer-text {
              font-size: 10px;
              padding: 4px 8px;
            }
          }
          
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>

      {/* Login Modal for Shared Conversations */}
      {showLoginModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
            textAlign: 'center',
            position: 'relative'
          }}>
            {/* Close button */}
            <button
              onClick={handleCloseLoginModal}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#f5f5f5'}
              onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              ×
            </button>

            {/* Icon */}
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #2241DD, #3b82f6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              boxShadow: '0 8px 24px rgba(34, 65, 221, 0.3)'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                <polyline points="10,17 15,12 10,7" />
                <line x1="15" y1="12" x2="3" y2="12" />
              </svg>
            </div>

            {/* Title */}
            <h2 style={{
              margin: '0 0 16px 0',
              fontSize: '24px',
              fontWeight: '600',
              color: '#1a1a1a'
            }}>
              Login Required
            </h2>

            {/* Description */}
            <p style={{
              margin: '0 0 32px 0',
              fontSize: '16px',
              lineHeight: '1.5',
              color: '#666',
              maxWidth: '320px'
            }}>
              To continue this shared conversation and start chatting, you need to log in to your account.
            </p>

            {/* Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={handleCloseLoginModal}
                style={{
                  padding: '12px 24px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#666',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  minWidth: '100px'
                }}
                onMouseOver={(e) => {
                  e.target.style.borderColor = '#ccc';
                  e.target.style.backgroundColor = '#f8f9fa';
                }}
                onMouseOut={(e) => {
                  e.target.style.borderColor = '#e0e0e0';
                  e.target.style.backgroundColor = 'white';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleLogin}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #2241DD, #3b82f6)',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  minWidth: '100px',
                  boxShadow: '0 4px 12px rgba(34, 65, 221, 0.3)'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(34, 65, 221, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(34, 65, 221, 0.3)';
                }}
              >
                Login
              </button>
            </div>
          </div>
        </div>
      )}

    </MathJaxContext>
  );
};

ConversationView.propTypes = {
  conversation: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string
  })
};

export default ConversationView;

/* Mermaid Diagram Styles */

const styles = document.createElement('style');
styles.innerHTML = `
  .mermaid-diagram-container {
    position: relative;
    background-color: #f9f9f9;
    border-radius: 8px;
    margin: 16px 0;
    padding: 40px 16px 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow-x: auto;
    transition: all 0.2s ease;
  }
  
  .mermaid-diagram-container::before {
    content: '';
    position: absolute;
    top: 30px;
    left: 30px;
    width: 15%;
    height: 30px;
    background-image: url(${Geohublogo});
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    z-index: 1;
  }
  
  .mermaid-diagram-container:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }
  
  .mermaid-diagram {
    width: 100%;
    display: flex;
    justify-content: center;
  }
  
  /* Ensure SVG fits within container */
  .mermaid-diagram svg {
    max-width: 100%;
    height: auto;
  }
  
  /* Text styling inside mermaid diagrams */
  .mermaid-diagram text,
  .mermaid-diagram tspan,
  .mermaid-diagram foreignObject p,
  .mermaid-diagram .messageText,
  .mermaid-diagram .labelText,
  .mermaid-diagram .loopText,
  .mermaid-diagram .nodeLabel {
    font-size: 10px !important;
    line-height: 18px !important;
    letter-spacing: 0.03em !important;
  }
  
  /* Label styling for better readability */
  .mermaid-diagram .label {
    font-size: 10px !important;
    line-height: 18px !important;
    letter-spacing: 0.03em !important;
    font-family: inherit !important;
  }
  
  /* Style for chart paragraph elements */
  .mermaid-diagram p {
    font-size: 10px !important;
    line-height: 18px !important;
    letter-spacing: 0.03em !important;
    margin: 2px 0 !important;
  }
  
  /* Add responsive behavior */
  @media (max-width: 768px) {
    .mermaid-diagram-container {
      padding: 40px 12px 12px;
      margin: 12px 0;
    }
  }
`;
document.head.appendChild(styles);

